/** Tezkor faollik yozish (qo'ng'iroq, izoh, SMS, email): o'ng tugma menyusidan, profilga o'tmasdan. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { useLogActivity } from "@/features/drivers/api";
import { CALL_OUTCOMES, type CallOutcome } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Field, NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

type ActivityType = "call" | "note" | "sms" | "email";
const TYPES: ActivityType[] = ["call", "note", "sms", "email"];

export function QuickActivityModal({ driver, type: initialType, open, onOpenChange }: {
  driver: { id: number; full_name: string };
  type: ActivityType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("menu");
  const { t: td } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const log = useLogActivity(driver.id);
  const [type, setType] = useState<ActivityType>(initialType);
  const [outcome, setOutcome] = useState<CallOutcome>("answered");
  const [minutes, setMinutes] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setOutcome("answered");
    setMinutes("");
    setBody("");
    setError(null);
  }, [open, initialType]);

  async function save() {
    if (type !== "call" && !body.trim()) {
      setError(t("activity.bodyRequired"));
      return;
    }
    try {
      await log.mutateAsync({
        type,
        body: body.trim(),
        duration_sec: type === "call" && minutes ? Number(minutes) * 60 : null,
        outcome: type === "call" ? outcome : "",
      });
      toast.success(t("activity.saved"));
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields?.body?.join(" ") ?? err.message) : tc("toast.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("activity.title", { name: driver.full_name })}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={log.isPending} onClick={save}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("activity.type")}>
            <Select value={type} onChange={(e) => setType(e.target.value as ActivityType)}>
              {TYPES.map((k) => (
                <option key={k} value={k}>
                  {t(`activity.types.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          {type === "call" && (
            <>
              <Field label={t("activity.outcome")}>
                <Select value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome)}>
                  {CALL_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {td(`profile.activity.outcomes.${o}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("activity.minutes")}>
                <NumberInput min={0} maxLength={3} value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="5" />
              </Field>
            </>
          )}
        </div>
        <Field label={t("activity.body")} error={error ?? undefined}>
          <Textarea
            autoFocus
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={type === "call" ? t("activity.placeholderCall") : t("activity.placeholderNote")}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void save();
            }}
          />
        </Field>
      </div>
    </Modal>
  );
}
